// Frozen modal ratio sets per body class + the stretched-string inharmonicity
// generator. Pure data + one pure function.

import type { BodyClass } from '@shared/index';

/**
 * Stretched-string partial ratio: partial_n = n * sqrt(1 + B*n^2).
 * B = 0 reduces to the harmonic series (n). Monotone increasing in n and in B.
 */
export function stretchedString(n: number, B: number): number {
  return n * Math.sqrt(1 + B * n * n);
}

/**
 * The four frozen modal ratio sets. Deeply frozen; mutation throws in strict mode.
 * stretched-string is generated at call time (depends on poreDensity), so its entry
 * here is a placeholder empty array — geometryToModes builds it via stretchedString().
 */
export const RATIO_SETS: Readonly<Record<BodyClass, readonly number[]>> = Object.freeze({
  'circular-plate': Object.freeze([1, 2.32, 3.91, 4.11, 5.95]),
  bell: Object.freeze([0.5, 1, 1.2, 1.5, 2, 2.5, 3]),
  'free-free-bar': Object.freeze([1, 2.756, 5.404, 8.933, 13.34]),
  'stretched-string': Object.freeze([]),
}) as Readonly<Record<BodyClass, readonly number[]>>;

/**
 * Alternative "sonic bases" a preset can select to override a diatom's body-class spectrum.
 * The body class still governs symmetry shaping; the family supplies the partial ratios.
 * These give each scene a distinct instrument-family timbre.
 */
export const SONIC_BASES: Readonly<Record<string, readonly number[]>> = Object.freeze({
  gamelan: Object.freeze([1, 2.34, 2.98, 4.16, 5.43, 6.79]), // inharmonic metallophone (beats)
  glass: Object.freeze([1, 2.01, 3.0, 4.02, 5.01, 6.0, 7.0]), // near-harmonic pure glass
  sub: Object.freeze([0.5, 1, 1.5, 2, 3, 4]), // octave-stacked, low emphasis
  organ: Object.freeze([1, 2, 3, 4, 5, 6, 8]), // additive harmonic, organ-like
  fm: Object.freeze([1, 1.414, 2, 2.414, 2.828, 4]), // metallic sqrt2 stack
});
